import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Navigation, ParkingCircle } from "lucide-react";
import useI18n from "../hooks/useI18n";
import locales from "../locales.json";
import { notify } from "../notify";

const API_BASE = "https://dodavkari.lexikonn.eu";
const API_CREATE = API_BASE+"/api/parking/create.php";
const API_CANCEL = API_BASE+"/api/parking/cancel.php";
const API_LIST = API_BASE+"/api/parking/list.php";

function useMarkerColor(key = "markerColor", def = "#34d399") {
    const [color, setColor] = useState(() => {
        try { return localStorage.getItem(key) || def; } catch { return def; }
    });
    useEffect(() => {
        const onStorage = (e) => { if (e.key === key) setColor(e.newValue || def); };
        const onCustom = (e) => { if (e?.detail) setColor(e.detail); };
        window.addEventListener("storage", onStorage);
        window.addEventListener("markerColor:update", onCustom);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("markerColor:update", onCustom);
        };
    }, [key, def]);
    return color;
}

function locateOnce(map) {
    return new Promise((resolve, reject) => {
        let resolved = false;
        const ok = (e) => {
            if (resolved) return;
            resolved = true;
            map.off("locationfound", ok);
            map.off("locationerror", fail);
            resolve([e.latlng.lat, e.latlng.lng]);
        };
        const fail = (e) => {
            if (resolved) return;
            resolved = true;
            map.off("locationfound", ok);
            map.off("locationerror", fail);
            reject(e);
        };
        map.once("locationfound", ok);
        map.once("locationerror", fail);
        try { map.locate({ setView: false, enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }); }
        catch (e) { fail(e); }
    });
}

function FirstFixCenter({ onFix }) {
    const map = useMap();
    useEffect(() => {
        let mounted = true;
        locateOnce(map).then((ll) => { if (mounted) onFix?.(ll); }).catch(() => {});
        return () => { mounted = false; };
    }, [map, onFix]);
    return null;
}

function LiveWatch({ onUpdate }) {
    const map = useMap();
    useEffect(() => {
        let active = true;
        const handler = (e) => { if (active) onUpdate?.([e.latlng.lat, e.latlng.lng]); };
        map.on("locationfound", handler);
        try { map.locate({ watch: true, setView: false, enableHighAccuracy: true, maximumAge: 0 }); } catch {}
        return () => { active = false; map.off("locationfound", handler); try { map.stopLocate(); } catch {} };
    }, [map, onUpdate]);
    return null;
}

function MapBinder({ mapRef, onReady, onUserMove }) {
    const map = useMapEvents({ dragstart: onUserMove, zoomstart: onUserMove });
    useEffect(() => { mapRef.current = map; onReady?.(map); }, [map, mapRef, onReady]);
    return null;
}

function Modal({ open, title, children, onClose }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[20000] grid place-items-center">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-[min(520px,92vw)] rounded-2xl border border-white/10 bg-slate-900 text-white p-4 shadow-xl">
                <div className="text-lg font-semibold mb-2">{title}</div>
                <div>{children}</div>
            </div>
        </div>
    );
}

function formatLocalInput(dt) {
    const pad = (n) => String(n).padStart(2, "0");
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mm = pad(dt.getMinutes());
    return `${y}-${m}-${d}T${hh}:${mm}`;
}

function addDays(dt, days) {
    const n = new Date(dt.getTime());
    n.setDate(n.getDate() + days);
    return n;
}

function useStoredParking() {
    const [state, setState] = useState(() => {
        try { return JSON.parse(localStorage.getItem("dk_parking") || "null"); } catch { return null; }
    });
    const save = useCallback((p) => {
        try { localStorage.setItem("dk_parking", JSON.stringify(p)); } catch {}
        setState(p);
    }, []);
    const clear = useCallback(() => {
        try { localStorage.removeItem("dk_parking"); } catch {}
        setState(null);
    }, []);
    return [state, save, clear];
}

function buildNavLinks(lat, lng) {
    const q = `${lat},${lng}`;
    return {
        apple: `http://maps.apple.com/?daddr=${q}&dirflg=d`,
        google: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
        waze: `https://waze.com/ul?ll=${q}&navigate=yes`
    };
}

export default function MapView({ user }) {
    const { t, lang } = useI18n(locales);
    const [me, setMe] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const [lastAutoCenterAt, setLastAutoCenterAt] = useState(0);
    const [userInteractedAt, setUserInteractedAt] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [presetMsg, setPresetMsg] = useState("");
    const [customMsg, setCustomMsg] = useState("");
    const [leaveAt, setLeaveAt] = useState(() => {
        const base = addDays(new Date(), 1);
        base.setHours(7, 0, 0, 0);
        return formatLocalInput(base);
    });
    const [activeParking, setActiveParking, clearActiveParking] = useStoredParking();
    const mapRef = useRef(null);
    const myLocationMarkerRef = useRef(null);
    const parkingLayerRef = useRef(null);
    const myParkingMarkerRef = useRef(null);
    const knownMarkers = useRef(new Map());
    const AUTO_COOLDOWN = 10000;
    const markerColor = useMarkerColor("markerColor", "#34d399");

    const myIcon = useMemo(() => {
        const svg = encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="42" viewBox="0 0 28 42">
        <path d="M14 42c-1-6-12-15-12-24C2 8 7.4 2 14 2s12 6 12 16c0 9-11 18-12 24z" fill="${markerColor}"/>
        <circle cx="14" cy="18" r="6.5" fill="#fff"/>
      </svg>`
        );
        return L.icon({ iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`, iconSize: [28, 42], iconAnchor: [14, 42], popupAnchor: [0, -38] });
    }, [markerColor]);

    const parkingIcon = useMemo(() => {
        const svg = encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="46" viewBox="0 0 28 42">
        <path d="M14 42c-1-6-12-15-12-24C2 8 7.4 2 14 2s12 6 12 16c0 9-11 18-12 24z" fill="${markerColor}"/>
        <circle cx="14" cy="18" r="7" fill="#000" opacity=".12"/>
        <text x="14" y="22" text-anchor="middle" font-size="12" font-family="ui-sans-serif,system-ui" fill="#fff">P</text>
      </svg>`
        );
        return L.icon({ iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`, iconSize: [30, 46], iconAnchor: [15, 46], popupAnchor: [0, -40] });
    }, [markerColor]);

    const handleFirstFix = useCallback((ll) => setMe(ll), []);
    const handleLiveUpdate = useCallback((ll) => setMe((prev) => (prev ? ll : prev)), []);
    const markUserMove = useCallback(() => setUserInteractedAt(Date.now()), []);

    const jumpTo = useCallback((ll, zoom = 17) => {
        const map = mapRef.current;
        if (!map) return;
        try { map.stop(); } catch {}
        try { map.flyTo(ll, Math.max(map.getZoom(), zoom), { duration: 0.45 }); }
        catch { map.setView(ll, Math.max(map.getZoom(), zoom)); }
    }, []);

    const findMe = useCallback(async () => {
        if (!mapRef.current) return;
        if (me) { jumpTo(me); setLastAutoCenterAt(Date.now()); return; }
        try {
            const ll = await locateOnce(mapRef.current);
            setMe(ll);
            jumpTo(ll);
            setLastAutoCenterAt(Date.now());
            return;
        } catch {}
        try {
            const m = mapRef.current;
            m.once("locationfound", (e) => {
                const ll = [e.latlng.lat, e.latlng.lng];
                setMe(ll);
                jumpTo(ll);
                setLastAutoCenterAt(Date.now());
            });
            m.once("locationerror", () => { if (me) { jumpTo(me); setLastAutoCenterAt(Date.now()); } });
            m.locate({ setView: false, enableHighAccuracy: true, timeout: 10000 });
        } catch {
            if (me) { jumpTo(me); setLastAutoCenterAt(Date.now()); }
        }
    }, [me, jumpTo]);

    useEffect(() => {
        if (!me || !mapRef.current) return;
        const now = Date.now();
        const idleLongEnough = now - userInteractedAt >= AUTO_COOLDOWN;
        const cooledDown = now - lastAutoCenterAt >= AUTO_COOLDOWN;
        if (idleLongEnough && cooledDown) {
            jumpTo(me);
            setLastAutoCenterAt(now);
        }
    }, [me, jumpTo, userInteractedAt, lastAutoCenterAt]);

    useEffect(() => {
        let brand = null;
        try { brand = localStorage.getItem("vanBrand") || null; } catch {}
        if (!mapReady || !mapRef.current || !me) return;
        const popupHtml =
            `<b>${user?.username || t("mobile.me")}</b>` +
            (brand ? `<br>${t("mobile.van")}: ${brand}` : `<br>${t("mobile.im_here")}`);
        if (myLocationMarkerRef.current) {
            myLocationMarkerRef.current.setLatLng(me).setIcon(myIcon).bindPopup(popupHtml);
            return;
        }
        myLocationMarkerRef.current = L.marker(me, { icon: myIcon }).addTo(mapRef.current).bindPopup(popupHtml);
    }, [me, mapReady, user, myIcon, t]);

    const ensureParkingLayer = useCallback(() => {
        if (!parkingLayerRef.current && mapRef.current) {
            parkingLayerRef.current = L.layerGroup().addTo(mapRef.current);
        }
        return parkingLayerRef.current;
    }, []);

    const handlePopupActions = useCallback((e) => {
        const root = e.popup.getElement();
        if (!root) return;
        const onClick = (evt) => {
            const a = evt.target.closest("[data-nav]");
            if (!a) return;
            const url = a.getAttribute("data-url");
            if (url) window.open(url, "_blank");
        };
        root.addEventListener("click", onClick);
        e.popup.once("remove", () => root.removeEventListener("click", onClick));
    }, []);

    const renderParkingPopup = useCallback((p) => {
        const name = p.user?.name || t("mobile.me");
        const brand = p.user?.brand ? `${t("mobile.van")}: ${p.user.brand}` : "";
        const until = new Date(p.until_iso).toLocaleString(lang === "cs" ? "cs-CZ" : lang === "sk" ? "sk-SK" : "en-GB");
        const msg = p.message ? `<div class="mt-1 opacity-90">${p.message}</div>` : "";
        const links = buildNavLinks(p.coords.lat, p.coords.lng);
        return `
            <div style="min-width:220px">
                <div><b>${name}</b></div>
                ${brand ? `<div>${brand}</div>` : ""}
                <div>${t("parking.leave_at")}: <b>${until}</b></div>
                ${msg}
                <div class="mt-2 grid grid-cols-3 gap-2">
                    <button class="px-2 py-1 rounded bg-slate-800 text-white text-xs" data-nav="apple" data-url="${links.apple}">${t("parking.nav_apple")}</button>
                    <button class="px-2 py-1 rounded bg-slate-800 text-white text-xs" data-nav="google" data-url="${links.google}">${t("parking.nav_google")}</button>
                    <button class="px-2 py-1 rounded bg-slate-800 text-white text-xs" data-nav="waze" data-url="${links.waze}">${t("parking.nav_waze")}</button>
                </div>
            </div>
        `;
    }, [t, lang]);

    const upsertMarker = useCallback((rec) => {
        const layer = ensureParkingLayer();
        const id = String(rec.id);
        let m = knownMarkers.current.get(id);
        if (m) {
            m.setLatLng([rec.coords.lat, rec.coords.lng]).setIcon(parkingIcon).bindPopup(renderParkingPopup(rec));
            return;
        }
        m = L.marker([rec.coords.lat, rec.coords.lng], { icon: parkingIcon });
        m.addTo(layer).bindPopup(renderParkingPopup(rec));
        m.on("popupopen", handlePopupActions);
        knownMarkers.current.set(id, m);
    }, [ensureParkingLayer, parkingIcon, renderParkingPopup, handlePopupActions]);

    const removeMarker = useCallback((id) => {
        const m = knownMarkers.current.get(String(id));
        if (!m) return;
        try { m.remove(); } catch {}
        knownMarkers.current.delete(String(id));
    }, []);

    const syncAround = useCallback(async (center) => {
        if (!center) return;
        const url = `${API_LIST}?lat=${encodeURIComponent(center[0])}&lng=${encodeURIComponent(center[1])}&radius_km=50`;
        try {
            const r = await fetch(url, { credentials: "include" });
            if (!r.ok) return;
            const data = await r.json();
            const ids = new Set();
            for (const rec of (data?.items || [])) {
                ids.add(String(rec.id));
                upsertMarker(rec);
            }
            for (const [k] of knownMarkers.current) {
                if (!ids.has(k)) removeMarker(k);
            }
        } catch {}
    }, [upsertMarker, removeMarker]);

    useEffect(() => {
        const timer = setInterval(() => { if (me) syncAround(me); }, 15000);
        return () => clearInterval(timer);
    }, [me, syncAround]);

    useEffect(() => { if (me) syncAround(me); }, [me, syncAround]);

    const createParking = useCallback(async (payload) => {
        try {
            const r = await fetch(API_CREATE, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
            if (!r.ok) throw new Error("create_failed");
            return await r.json();
        } catch (e) { return { ok: false }; }
    }, []);

    const cancelParking = useCallback(async (id) => {
        try {
            const r = await fetch(API_CANCEL, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ id }) });
            if (!r.ok) throw new Error("cancel_failed");
            return await r.json();
        } catch { return { ok: false }; }
    }, []);

    const openParkDialog = useCallback(async () => {
        if (activeParking) {
            setCancelOpen(true);
            return;
        }
        if (!me && mapRef.current) {
            try { const ll = await locateOnce(mapRef.current); setMe(ll); } catch {}
        }
        if (!me) { alert(t("mobile.park_no_gps")); return; }
        setDialogOpen(true);
    }, [activeParking, t, me]);

    const doCancel = useCallback(async () => {
        if (!activeParking) return;
        const res = await cancelParking(activeParking.id);
        if (res?.ok) {
            removeMarker(activeParking.id);
            clearActiveParking();
            setCancelOpen(false);
            await notify(t("app.name"), { body: t("parking.canceled") });
        }
    }, [activeParking, cancelParking, removeMarker, clearActiveParking, t]);

    const submitParking = useCallback(async () => {
        const when = new Date(leaveAt);
        if (Number.isNaN(when.getTime())) return;
        const brand = (() => { try { return localStorage.getItem("vanBrand") || null; } catch { return null; } })();
        const message = customMsg.trim().length ? customMsg.trim() : presetMsg || "";
        const payload = {
            user: { name: user?.username || null, color: markerColor || null, brand: brand || null },
            coords: { lat: me[0], lng: me[1] },
            until_iso: when.toISOString(),
            message
        };
        const res = await createParking(payload);
        if (res?.ok && res.id) {
            const rec = { id: res.id, ...payload };
            setActiveParking(rec);
            upsertMarker(rec);
            setDialogOpen(false);
            setPresetMsg("");
            setCustomMsg("");
            await notify(t("app.name"), { body: t("parking.created") });
        }
    }, [leaveAt, customMsg, presetMsg, user, me, markerColor, createParking, setActiveParking, upsertMarker, t]);

    const presets = useMemo(() => [
        t("parking.msg_coffee"),
        t("parking.msg_chat"),
        t("parking.msg_need_help"),
        t("parking.msg_loading"),
        t("parking.msg_break")
    ], [t]);

    return (
        <div className="relative overflow-hidden" style={{ height: "calc(100dvh - (56px + env(safe-area-inset-top, 0px)))" }}>
            <MapContainer center={me || [50.0755, 14.4378]} zoom={12} className="h-full w-full" whenCreated={(m) => { mapRef.current = m; setMapReady(true); }}>
                <MapBinder mapRef={mapRef} onReady={() => setMapReady(true)} onUserMove={markUserMove} />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
                <FirstFixCenter onFix={handleFirstFix} />
                <LiveWatch onUpdate={handleLiveUpdate} />
            </MapContainer>

            <div className="absolute right-3 bottom-3 z-[12000] flex flex-col gap-2 pointer-events-auto" style={{ marginRight: "env(safe-area-inset-right, 0px)", marginBottom: "env(safe-area-inset-bottom, 0px)" }}>
                <button type="button" onClick={findMe} disabled={!mapReady} className={`px-3 py-2 rounded-xl border text-sm inline-flex items-center justify-center ${mapReady ? "bg-slate-900 border-white/10 hover:bg-slate-800" : "bg-slate-800/60 border-white/10 opacity-60 cursor-not-allowed"}`} aria-label={t("mobile.find_me")} title={t("mobile.find_me")}>
                    <Navigation className="w-5 h-5" />
                </button>
                <button type="button" onClick={openParkDialog} disabled={!mapReady} className={`px-3 py-2 rounded-xl border text-sm font-medium inline-flex items-center justify-center gap-2 ${mapReady ? (activeParking ? "bg-rose-500/90 hover:bg-rose-500 border-rose-400 text-black" : "bg-emerald-500/90 hover:bg-emerald-500 border-emerald-400 text-black") : "bg-emerald-500/40 border-white/10 opacity-60 cursor-not-allowed"}`}>
                    <ParkingCircle className="w-5 h-5" />
                    {activeParking ? t("parking.cancel") : t("mobile.park")}
                </button>
            </div>

            <Modal open={dialogOpen} title={t("parking.title")} onClose={() => setDialogOpen(false)}>
                <div className="grid gap-3">
                    <label className="grid gap-1">
                        <span className="text-sm opacity-90">{t("parking.leave_at")}</span>
                        <input type="datetime-local" value={leaveAt} onChange={(e) => setLeaveAt(e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-emerald-400" />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-sm opacity-90">{t("parking.note_opt")}</span>
                        <select value={presetMsg} onChange={(e) => setPresetMsg(e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2">
                            <option value="">{t("parking.none")}</option>
                            {presets.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </label>
                    <label className="grid gap-1">
                        <span className="text-sm opacity-90">{t("parking.custom_msg")}</span>
                        <input type="text" value={customMsg} onChange={(e) => setCustomMsg(e.target.value)} placeholder={t("parking.custom_ph")} className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-emerald-400" />
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button onClick={() => setDialogOpen(false)} className="px-3 py-2 rounded-lg bg-slate-800 border border-white/10">{t("common.cancel")}</button>
                        <button onClick={submitParking} className="px-3 py-2 rounded-lg bg-emerald-500 text-black border border-emerald-400">{t("parking.start")}</button>
                    </div>
                </div>
            </Modal>

            <Modal open={cancelOpen} title={t("parking.cancel")} onClose={() => setCancelOpen(false)}>
                <div className="grid gap-4">
                    <div className="opacity-90">{t("parking.cancel_confirm")}</div>
                    <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setCancelOpen(false)} className="px-3 py-2 rounded-lg bg-slate-800 border border-white/10">{t("common.cancel")}</button>
                        <button onClick={doCancel} className="px-3 py-2 rounded-lg bg-rose-500 text-black border border-rose-400">{t("parking.cancel")}</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
